import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { CreatePatientRequest, PatientResponse } from '../../models/patient.model';
import { PatientsService } from '../../services/patients.service';

export type PatientModalMode = 'add' | 'edit';

@Component({
  selector: 'app-patient-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    TextareaModule,
    CheckboxModule
  ],
  templateUrl: './patient-modal.component.html',
  styleUrls: ['./patient-modal.component.scss']
})
export class PatientModalComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() mode: PatientModalMode = 'add';
  @Input() visible: boolean = false;
  @Input() patient: PatientResponse | null = null;
  /** Prefill first/last name when opening add mode (e.g. from landing search). */
  @Input() seedName: string = '';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() patientSaved = new EventEmitter<PatientResponse>();

  patientForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private patientsService: PatientsService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}
  
  // Form options - initialize with default values to prevent expression change errors
  sexOptions: any[] = [
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
    { label: 'Other', value: 'OTHER' },
    { label: 'Unknown', value: 'UNKNOWN' }
  ];
  // Physician dropdown removed - now using text input
  hasChanges = signal(false);
  originalFormData: any = null;

  ngOnInit() {
    // Initialize form immediately to prevent FormGroup error
    this.initForm();
    
        // If we're in edit mode and have patient data, populate the form
        if (this.mode === 'edit' && this.patient) {
          setTimeout(() => this.populateForm(), 0);
        }
  }

  ngAfterViewInit() {
    // Additional check after view is initialized
    if (this.mode === 'edit' && this.patient && this.visible) {
      setTimeout(() => this.populateForm(), 100);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['patient'] && this.patient && this.mode === 'edit') {
      // Ensure form is initialized before populating
      if (this.patientForm) {
        this.populateForm();
      } else {
        // If form is not initialized yet, wait for next tick
        setTimeout(() => this.populateForm(), 0);
      }
    }
    
    if (changes['visible'] && this.visible) {
      if (this.mode === 'add') {
        this.resetForm();
        this.applySeedName();
      } else if (this.mode === 'edit' && this.patient) {
        setTimeout(() => this.populateForm(), 0);
      }
    }

    if (changes['seedName'] && this.visible && this.mode === 'add') {
      this.applySeedName();
    }
  }

  private applySeedName(): void {
    if (!this.patientForm) {
      return;
    }
    const raw = (this.seedName ?? '').trim();
    if (!raw) {
      return;
    }
    const parts = raw.split(/\s+/).filter(Boolean);
    const firstName = parts[0] ?? '';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
    this.patientForm.patchValue({ firstName, lastName });
  }

  private initForm() {
    this.patientForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      dateOfBirth: [null], // Optional
      sex: [''], // Optional
      phoneNumber: [''], // Optional
      email: ['', [Validators.email]], // Optional but must be valid email if provided
      streetAddress: [''],
      city: [''],
      state: [''],
      zipCode: [''],
          allergies: [''],
          primaryPhysician: [''],
          physicianContactNumber: [''],
          insuranceName: [''], // Optional
      insurancePolicyNumber: [''], // Optional
      insurancePhone: [''],
      active: [true] // Default to active for new patients
    });

    // No longer needed - physician is now a text input

    // Watch for form changes in edit mode
    this.patientForm.valueChanges.subscribe(() => {
      if (this.mode === 'edit' && this.originalFormData) {
        this.checkForChanges();
      }
    });
  }

  public populateForm() {
    if (this.patient && this.patientForm) {
      let formData: any = {};


      // Now we only work with PatientResponse from backend
      const patientResponse = this.patient as PatientResponse;
      formData = {
        firstName: patientResponse.firstName || '',
        lastName: patientResponse.lastName || '',
        dateOfBirth: patientResponse.dateOfBirth || '',
        sex: patientResponse.sex || '',
        phoneNumber: patientResponse.phoneNumber || '',
        email: patientResponse.email || '',
        streetAddress: patientResponse.streetAddress || '',
        city: patientResponse.city || '',
        state: patientResponse.state || '',
        zipCode: patientResponse.zipCode || '',
        allergies: patientResponse.allergies || '',
        primaryPhysician: patientResponse.primaryPhysician || '',
        physicianContactNumber: patientResponse.physicianContactNumber || '',
        insuranceName: patientResponse.insuranceName || '',
        insurancePolicyNumber: patientResponse.insurancePolicyNumber || '',
        insurancePhone: patientResponse.insurancePhone || '',
        active: patientResponse.active !== undefined ? patientResponse.active : true
      };
      
      
      // Store original form data for change detection
      this.originalFormData = { ...formData };
      
      // Use setValue instead of patchValue for more reliable population
      this.patientForm.setValue(formData);
      
      // Reset change detection
      this.hasChanges.set(false);
      
      // Also trigger change detection
      this.patientForm.markAsTouched();
      this.patientForm.updateValueAndValidity();
    }
  }

  private resetForm() {
    this.patientForm.reset();
    this.hasChanges.set(false);
    this.originalFormData = null;
  }

  private checkForChanges() {
    if (!this.originalFormData || !this.patientForm) {
      this.hasChanges.set(false);
      return;
    }

    const currentFormData = this.patientForm.value;
    const hasChanges = Object.keys(this.originalFormData).some(key => {
      const originalValue = this.originalFormData[key];
      const currentValue = currentFormData[key];
      
      // Handle null/undefined comparisons
      if (originalValue === null || originalValue === undefined) {
        return currentValue !== null && currentValue !== undefined && currentValue !== '';
      }
      if (currentValue === null || currentValue === undefined) {
        return originalValue !== null && originalValue !== undefined && originalValue !== '';
      }
      
      return originalValue !== currentValue;
    });

    this.hasChanges.set(hasChanges);
  }

  onSubmit() {
    if (this.patientForm.valid) {
      const formValue = this.patientForm.value;
      
      const patientRequest: CreatePatientRequest = {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        dateOfBirth: formValue.dateOfBirth || undefined,
        sex: formValue.sex || undefined,
        phoneNumber: formValue.phoneNumber || undefined,
        email: formValue.email || undefined,
        streetAddress: formValue.streetAddress || undefined,
        city: formValue.city || undefined,
        state: formValue.state || undefined,
        zipCode: formValue.zipCode || undefined,
        allergies: formValue.allergies || undefined,
        primaryPhysician: formValue.primaryPhysician || undefined,
        physicianContactNumber: formValue.physicianContactNumber || undefined,
        insuranceName: formValue.insuranceName || undefined,
        insurancePolicyNumber: formValue.insurancePolicyNumber || undefined,
        insurancePhone: formValue.insurancePhone || undefined,
        active: formValue.active
      };



      if (this.mode === 'add') {
        // Create new patient using backend API
        this.patientsService.createPatient(patientRequest).subscribe({
          next: (patient) => {
            this.patientSaved.emit(patient);
            this.onCancel();
          },
          error: (error) => {
            console.error('Error adding patient:', error);
            // You might want to show an error message to the user here
          }
        });
      } else {
        // Update existing patient using backend API
        if (this.patient && 'id' in this.patient) {
          const patientId = (this.patient as PatientResponse).id;
          this.patientsService.updatePatient(patientId, patientRequest).subscribe({
            next: (patient) => {
              this.patientSaved.emit(patient);
              this.onCancel();
            },
            error: (error) => {
              console.error('Error updating patient:', error);
              // You might want to show an error message to the user here
            }
          });
        } else {
          console.error('Cannot update patient: invalid patient data');
        }
      }
    }
  }

  onCancel() {
    this.visibleChange.emit(false);
    this.resetForm();
  }

  private generateMRN(): string {
    return Math.floor(Math.random() * 100000).toString().padStart(6, '0');
  }

  private calculateAge(dateOfBirth: string): number {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  get modalTitle(): string {
    return this.mode === 'add' ? 'Add New Patient' : 'Edit Patient';
  }

  get submitButtonLabel(): string {
    return this.mode === 'add' ? 'Add Patient' : 'Save Changes';
  }
}
